import { useContext, useEffect, useState } from 'react'
import { Column, Row } from 'react-table'
import { IVanPartial } from '../../../../app/entities/Van'
import Table from '../../global/components/table/Table'
import BasicFilter from '../../global/components/table/helpers/BasicFilter'
import NoFilter from '../../global/components/table/helpers/NoFilter'
import { handleColumnFilterUpdate } from '../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../global/components/table/helpers/retrieveData'
import { ILocationFilters } from '../../work-orders/components/main/WorkOrdersMain'
import { IRefreshCounterProps } from '../main/VansMain'
import EditVanModal from '../modal/edit-van-modal/main/EditVanModal'
import hasPermission from '../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../global/constants/permissions'
import { UserContext } from '../../base/components/base-container/BaseContainer'
import { VanContext } from '../../global/context/van-context/VanContext'
import { formatDateYyyyMmDd } from '../../global/utils/date/date-utils-yyyy-mm-dd'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import createSearchParamsString from '../../global/utils/createSearchParamsString'
import getBatteryDataForVan from '../../global/utils/get-battery-data-for-van'

function safelyConvertToNumber(
  value: string | undefined | null,
): number | undefined {
  if (value === undefined || value === null) {
    return undefined
  } else {
    return parseFloat(value)
  }
}

interface IVansTableProps {
  locationFilters: ILocationFilters
  refresh: IRefreshCounterProps
}

function getBatteryTextColor(stateOfCharge: number) {
  if (stateOfCharge > 80) {
    return 'black'
  } else if (stateOfCharge > 40) {
    return '#FFC107' //yellow with contrast for white background
  } else {
    return 'red'
  }
}

function accessorForBatteries(van: IVanPartial) {
  const batteryData = getBatteryDataForVan(van)

  if (batteryData.length > 0) {
    const batteryOneStateOfCharge: number | undefined = safelyConvertToNumber(
      batteryData[0]?.stateOfCharge,
    )
    const batteryTwoStateOfCharge: number | undefined =
      batteryData.length > 1
        ? safelyConvertToNumber(batteryData[1]?.stateOfCharge)
        : undefined
    if (batteryOneStateOfCharge === undefined) {
      return ''
    }
    return (
      <>
        <div style={{ color: getBatteryTextColor(batteryOneStateOfCharge) }}>
          {batteryOneStateOfCharge}%
        </div>
        <div
          style={{
            color: getBatteryTextColor(batteryTwoStateOfCharge ?? 100),
          }}
        >
          {batteryTwoStateOfCharge ? `${batteryTwoStateOfCharge}%` : ''}
        </div>
      </>
    )
  }
  return ''
}

function VansTable(props: IVansTableProps) {
  const { user } = useContext(UserContext)
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const { clearVanData, queryVanData } = useContext(VanContext)

  const {
    locationFilters: { locationFilter },
    refresh: { refreshCounter, setRefreshCounter },
  } = props

  const [tableState, setTableState] = useState({
    data: [] as IVanPartial[],
    isLoading: false,
    pageSize: 100,
    offSet: 0,
    count: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})

  const isVansEditDisabled = !hasPermission(user).allow(
    PERMISSIONS.VANS.CATEGORY,
    PERMISSIONS.VANS.ENTRIES.EDIT_VAN_DETAILS.NAME,
  )

  async function handleOpen(id: string) {
    setIsModalOpen(true)
    queryVanData(id)

    const queryStringParams = await createSearchParamsString(searchParams)
    const url = `/vans/${id}` + queryStringParams
    navigate(url)
  }

  useEffect(() => {
    if (id && !isModalOpen) {
      handleOpen(id)
    }
  }, [id])

  function handleClosed() {
    setIsModalOpen(false)
    navigate('/vans')
    clearVanData()
    retrieveDataWrapper()
  }

  const getAllVansEndpoint = `${process.env.REACT_APP_ORIGIN}/vans?`
  function retrieveDataWrapper() {
    retrieveData(setTableState, {
      endpoint: getAllVansEndpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      locationId: locationFilter,
      sortBy: 'mostRecentBatteryData',
      columnFilters,
    })
  }

  useEffect(retrieveDataWrapper, [
    locationFilter,
    columnFilters,
    tableState.pageSize,
    tableState.offSet,
    refreshCounter,
  ])

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  const columns: Column<any>[] = [
    {
      Header: 'Van Name',
      accessor: 'name',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ name: val }),
      }),
    },
    {
      Header: 'Location',
      accessor: (data: IVanPartial) => {
        return data?.location?.name
      },
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ location: { name: val } }),
      }),
    },
    {
      Header: 'Inventory Status',
      accessor: (data: IVanPartial) => data.inventoryStatus,
      Filter: NoFilter,
    },
    {
      Header: 'Batteries',
      accessor: (data: IVanPartial) => accessorForBatteries(data),
      Filter: NoFilter,
    },
    {
      Header: 'Operating Status',
      accessor: (data: IVanPartial) => data.operatingStatus,
      Filter: NoFilter,
    },
    {
      Header: 'Operating Status Start Date',
      accessor: (data: IVanPartial) => {
        const date = data.operatingStatusStartDate
        const cleanedTime = date ? formatDateYyyyMmDd(new Date(date)) : ''
        return date && cleanedTime
      },
      Filter: NoFilter,
    },

    {
      Header: 'DOT End Date',
      accessor: (data: IVanPartial) => {
        const date = data.dotEndDate
        const cleanedTime = date ? formatDateYyyyMmDd(new Date(date)) : ''
        return date && cleanedTime
      },
      Filter: NoFilter,
    },
    {
      Header: 'Defects',
      accessor: (data: IVanPartial) => {
        return (data?.vir ?? [])[0]?.defects ? 'Yes' : 'No'
      },
      Filter: NoFilter,
    },
    {
      Header: 'Last Daily',
      accessor: (data: IVanPartial) => {
        const date = data.lastDvir
        const cleanedTime = date ? formatDateYyyyMmDd(new Date(date)) : ''
        return date && cleanedTime
      },
      Filter: NoFilter,
    },
    {
      Header: 'Last Weekly',
      accessor: (data: IVanPartial) => {
        const date = data.lastWvir
        const cleanedTime = date ? formatDateYyyyMmDd(new Date(date)) : ''
        return date && cleanedTime
      },
      Filter: NoFilter,
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <EditVanModal
        isOpen={isModalOpen}
        handleClosed={handleClosed}
        refresh={{ refreshCounter, setRefreshCounter }}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={
          isVansEditDisabled
            ? () => {}
            : (row: Row) => {
                const original: any = row.original
                const selectedVan = tableState.data.find(
                  (data: IVanPartial) => data.objectId === original.objectId,
                )
                if (selectedVan) {
                  handleOpen(original.objectId)
                }
              }
        }
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default VansTable
